import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteFooter from "../../general/components/layout/SiteFooter";
import useRevealOnScroll from "../../general/components/common/useRevealOnScroll";
import { routes } from "../../general/routes/newsApi";
import FispTvNavbar from "../components/FispTvNavbar";
import { fetchCategoryPageNewsPage, findSegmentBySlug } from "../data/noticias";

function NewsLink({ item, className, children }) {
    if (item.external) {
        return (
            <a href={item.href} target="_blank" rel="noreferrer" className={className}>
                {children}
            </a>
        );
    }

    return (
        <Link to={item.href} className={className}>
            {children}
        </Link>
    );
}

function SegmentState({ title, description }) {
    return (
        <section className="tv-directory-shell">
            <div className="tv-state-card">
                <h2>{title}</h2>
                <p>{description}</p>
            </div>
        </section>
    );
}

function SegmentLoading() {
    return (
        <section className="tv-directory-shell tv-category-loading" role="status" aria-live="polite">
            <span className="tv-loading-announcer">Cargando seccion</span>

            <div className="tv-directory-hero tv-directory-hero-loading">
                <div className="tv-skeleton tv-directory-breadcrumb-skeleton"></div>
                <div className="tv-skeleton tv-directory-title-skeleton"></div>
                <div className="tv-skeleton tv-directory-copy-skeleton"></div>
                <div className="tv-skeleton tv-directory-copy-skeleton is-short"></div>
            </div>

            <div className="tv-section-feed">
                {[0, 1, 2, 3].map((item) => (
                    <div key={item} className="tv-section-feed-item tv-section-feed-item-loading">
                        <div className="tv-skeleton tv-section-feed-media-skeleton"></div>

                        <div className="tv-section-feed-copy tv-section-feed-copy-loading">
                            <div className="tv-skeleton tv-section-feed-title-skeleton"></div>
                            <div className="tv-skeleton tv-section-feed-line-skeleton"></div>
                            <div className="tv-skeleton tv-section-feed-line-skeleton is-short"></div>
                            <div className="tv-skeleton tv-section-feed-meta-skeleton"></div>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

function formatMeta(item) {
    if (item?.publishedAt) {
        return new Date(item.publishedAt).toLocaleDateString("es-AR");
    }

    return item?.category || "FISP TV";
}

export default function SegmentoCategoria() {
    const pageRef = useRef(null);
    const { slug } = useParams();
    const resolvedSegment = findSegmentBySlug(slug);
    const segment = resolvedSegment?.visible === false ? null : resolvedSegment;
    const [server, setServer] = useState(routes.Vercel);
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(1);
    const [status, setStatus] = useState(segment ? "loading" : "missing");
    const [hasMore, setHasMore] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    useRevealOnScroll(pageRef);

    useEffect(() => {
        let active = true;

        fetch(`${routes.Hostinger}/ping`)
            .then(async (response) => {
                const data = await response.text();

                if (!active) {
                    return;
                }

                if (data === "pong") {
                    setServer(routes.Hostinger);
                } else {
                    setServer(routes.Vercel);
                }
            })
            .catch(() => {
                if (active) {
                    setServer(routes.Vercel);
                }
            });

        return () => {
            active = false;
        };
    }, []);

    useEffect(() => {
        if (!segment || segment.slug === "ultimas") {
            setStatus("missing");
            return undefined;
        }

        const controller = new AbortController();

        async function loadFirstPage() {
            setStatus("loading");
            setItems([]);
            setPage(1);
            setHasMore(true);

            try {
                const result = await fetchCategoryPageNewsPage(segment.label, 1, 10, controller.signal, server);

                setItems(result.items);
                setHasMore(result.hasMore);
                setStatus("ready");
            } catch (error) {
                if (error.name !== "AbortError") {
                    console.error("Error cargando segmento:", error);
                    setStatus("error");
                }
            }
        }

        loadFirstPage();

        return () => controller.abort();
    }, [segment, server]);

    const handleLoadMore = async () => {
        if (!segment || !server || isLoadingMore || !hasMore) {
            return;
        }

        const nextPage = page + 1;
        setIsLoadingMore(true);

        try {
            const result = await fetchCategoryPageNewsPage(segment.label, nextPage, 10, undefined, server);
            setItems((currentItems) => [...currentItems, ...result.items]);
            setPage(nextPage);
            setHasMore(result.hasMore);
        } catch (error) {
            console.error("Error cargando mas noticias:", error);
        } finally {
            setIsLoadingMore(false);
        }
    };

    return (
        <div className="fisp-tv tv-category-page" ref={pageRef}>
            <FispTvNavbar activeCategory={segment?.label || ""} />

            {status === "missing" ? <SegmentState title="Segmento no encontrado" description="La seccion que buscas no esta disponible en FISP TV." /> : null}
            {status === "error" ? <SegmentState title="No se pudo cargar la seccion" description="Hubo un problema consultando las noticias de este segmento." /> : null}
            {status === "loading" ? <SegmentLoading /> : null}

            {status === "ready" && segment ? (
                <section className="tv-directory-shell">
                    <div className="tv-directory-hero reveal">
                        <div className="tv-directory-breadcrumb">
                            <Link to="/">Inicio</Link>
                            <span>/</span>
                            <Link to="/fisp-tv">FISP TV</Link>
                            <span>/</span>
                            <Link to="/fisp-tv/segmentos">Segmentos</Link>
                            <span>/</span>
                            <span>{segment.label}</span>
                        </div>
                        <h1>{segment.label}</h1>
                        <p>{segment.description}</p>
                    </div>

                    {items.length ? (
                        <>
                            <div className="tv-section-feed">
                                {items.map((item, index) => (
                                    <NewsLink
                                        key={item.slug || `${item.title}-${index}`}
                                        item={item}
                                        className="tv-section-feed-item"
                                    >
                                        <div className="tv-section-feed-media">
                                            {item.image ? <img src={item.image} alt={item.title} /> : <div className={`ph bg${(index % 10) + 1}`}></div>}
                                        </div>

                                        <div className="tv-section-feed-copy">
                                            <h2>{item.title}</h2>
                                            <p>{item.description || "Contenido disponible en FISP TV."}</p>
                                            <span className="tv-section-feed-meta">{formatMeta(item)}</span>
                                        </div>
                                    </NewsLink>
                                ))}
                            </div>

                            {hasMore ? (
                                <div className="tv-category-actions">
                                    <button type="button" className="tv-load-more" onClick={handleLoadMore} disabled={isLoadingMore}>
                                        {isLoadingMore ? "Cargando..." : "Cargar mas"}
                                    </button>
                                </div>
                            ) : null}
                        </>
                    ) : (
                        <div className="tv-category-empty">
                            <p>Todavia no hay noticias publicadas en esta seccion.</p>
                        </div>
                    )}
                </section>
            ) : null}

            <SiteFooter variant="tv" description="Multimedio de salud digital con entrevistas, informes, podcasts y coberturas especiales." />
        </div>
    );
}
